// Market Hlídač — pasti a příležitosti z dat + Prediction Modelu v2.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Siren, Sparkle, TrendUp, TrendDown } from "@phosphor-icons/react";
import { getMarketWatch } from "../lib/api";

const CONF_LABEL = { HIGH: "vysoká", MEDIUM: "střední", LOW: "nízká" };

function WatchCard({ p, kind }) {
    const accent = kind === "gem" ? "#9EE8B4" : "#FF8800";
    return (
        <div className="border border-white/10 bg-[#0d0d0d] p-3 hover:border-white/25 transition-colors"
             data-testid={`watch-${kind}-${p.fpl_id}`}
             style={{ borderLeft: `3px solid ${accent}` }}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                    <Link to={`/players/${p.fpl_id}`} className="font-display font-black text-sm hover:text-fa-mint">{p.name}</Link>
                    <span className="font-mono text-[9px] text-white/45 ml-2">{p.team} · {p.position} · £{p.price?.toFixed(1)}</span>
                </div>
                <div className="font-mono text-[10px] tabular-nums text-white/70">
                    pred <span className="font-bold" style={{ color: accent }}>{p.predicted_total}</span> b / 4 GW
                    <span className="text-white/40 ml-2">vl. {p.ownership_pct}%</span>
                </div>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
                {p.reasons.map((rr, i) => (
                    <span key={i} className="font-mono text-[9px] px-1.5 py-0.5 border"
                          style={{ borderColor: `${accent}55`, color: accent }}>{rr}</span>
                ))}
            </div>
            <div className="font-mono text-[8px] text-white/35 mt-1.5 flex gap-3">
                <span>jistota {CONF_LABEL[p.confidence] || p.confidence}</span>
                <span>minuty {Math.round(p.minutes_prob * 100)} %</span>
                {p.avg_fdr != null && <span>Ø FDR {p.avg_fdr}</span>}
                {(p.transfers_in > 0 || p.transfers_out > 0) && (
                    <span>{p.transfers_in.toLocaleString()} in / {p.transfers_out.toLocaleString()} out</span>
                )}
            </div>
        </div>
    );
}

export const MarketWatch = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getMarketWatch()
            .then(setData)
            .catch(() => toast.error("Načtení hlídače selhalo"))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="text-white/40 font-mono text-sm">Hlídač analyzuje trh…</div>;
    if (!data) return null;

    return (
        <div className="space-y-4" data-testid="market-watch">
            <p className="font-mono text-[10px] text-white/55">
                GW {data.gw_from}–{data.gw_to} · signály z Prediction Modelu v2 (FDR profily, konzistence, fáze, minuty) × poptávka trhu
                {data.preseason_mode && <span className="text-fa-yellow"> · pre-season režim (transfery ještě neběží, poptávka = vlastnictví)</span>}
            </p>
            <div className="grid lg:grid-cols-2 gap-5">
                <section className="space-y-2">
                    <h2 className="font-display font-black uppercase text-base flex items-center gap-1.5 text-fa-mint">
                        <Sparkle size={14} weight="fill" /> Příležitosti <TrendUp size={12} />
                    </h2>
                    <p className="font-mono text-[9px] text-white/40">Silná data, trh je zatím nevidí — nebo je vyhazuje.</p>
                    {data.gems.map((p) => <WatchCard key={p.fpl_id} p={p} kind="gem" />)}
                    {data.gems.length === 0 && <p className="font-mono text-[10px] text-white/40">Žádné výrazné příležitosti.</p>}
                </section>
                <section className="space-y-2">
                    <h2 className="font-display font-black uppercase text-base flex items-center gap-1.5 text-[#FF8800]">
                        <Siren size={14} weight="fill" /> Pozor, možné pasti <TrendDown size={12} />
                    </h2>
                    <p className="font-mono text-[9px] text-white/40">Hodně kupovaní / vlastnění hráči, u kterých data varují.</p>
                    {data.traps.map((p) => <WatchCard key={p.fpl_id} p={p} kind="trap" />)}
                    {data.traps.length === 0 && <p className="font-mono text-[10px] text-white/40">Žádné pasti nenalezeny.</p>}
                </section>
            </div>
        </div>
    );
};

export default MarketWatch;
